/**
 * React Query mutation hook for saving TikTok product changes.
 * Reads dirty state from useTikTokEditStore, calls save_changes API,
 * and handles cache invalidation + save status updates.
 *
 * Includes a transformer that converts flat dirty fields (price, sale_price,
 * inventory_quantity) into the structured format the backend expects
 * (price_changes, inventory_changes).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tiktokProductApi } from "../api/tiktokProductApi";
import type { SaveChangesResponse } from "../api/tiktokProductApi";
import { useTikTokEditStore } from "../stores/useTikTokEditStore";
import { parseJsonField } from "../widgets/shared";

/** Map backend save_type to user-facing message */
function getSaveMessage(saveType: SaveChangesResponse["save_type"]): string {
  switch (saveType) {
    case "instant_only":
      return "Saved";
    case "audited_only":
      return "Saved — pending TikTok review";
    case "mixed":
      return "Saved — some changes pending review";
    case "none":
      return "No changes to save";
    default:
      return "Saved";
  }
}

/**
 * Transform flat dirty fields from zustand store into the structured format
 * that save_changes() expects for price and inventory APIs.
 *
 * Store fields:        Backend expects:
 * price (number)    → price_changes: [{id, price: {amount, currency, sale_price?}}]
 * sale_price (num)  → (merged into price_changes above)
 * inventory_quantity → inventory_changes: [{id, inventory: [{warehouse_id, quantity}]}]
 * brand_name        → (removed — display-only, not sent to backend)
 */
function transformDirtyForSave(
  dirtyFields: Record<string, any>,
  formData: Record<string, any>,
): Record<string, any> {
  const transformed = { ...dirtyFields };

  // Extract SKU info from formData for price/inventory APIs
  const skusData = parseJsonField<any[]>(formData.skus_data);
  const primarySku = skusData?.[0];
  const skuId = primarySku?.id || formData.sku_id || "";

  // Transform price/sale_price → price_changes
  if ("price" in transformed || "sale_price" in transformed) {
    const priceObj: Record<string, string> = {
      amount: String(transformed.price ?? formData.price ?? 0),
      currency: formData.currency || "THB",
    };
    // Include sale_price if dirty (inside the price object, per TikTok API format)
    if ("sale_price" in transformed) {
      const sp = transformed.sale_price;
      if (sp && Number(sp) > 0) {
        priceObj.sale_price = String(sp);
      }
    }
    transformed.price_changes = [{ id: skuId, price: priceObj }];
    delete transformed.price;
    delete transformed.sale_price;
  }

  // Transform inventory_quantity → inventory_changes
  if ("inventory_quantity" in transformed) {
    const warehouseId = primarySku?.inventory?.[0]?.warehouse_id || "";
    transformed.inventory_changes = [{
      id: skuId,
      inventory: [{ warehouse_id: warehouseId, quantity: Number(transformed.inventory_quantity) }],
    }];
    delete transformed.inventory_quantity;
  }

  // Remove display-only fields that shouldn't be sent to backend
  delete transformed.brand_name;

  return transformed;
}

export function useTikTokSave() {
  const queryClient = useQueryClient();

  return useMutation<SaveChangesResponse, Error, void>({
    mutationFn: async () => {
      const state = useTikTokEditStore.getState();
      const { connectionName, productId, dirtyFields, validationErrors, _formData } = state;

      if (!connectionName || !productId) {
        throw new Error("Missing connection or product ID");
      }
      if (Object.keys(dirtyFields).length === 0) {
        return { status: "noop", save_type: "none" } as SaveChangesResponse;
      }

      // Block save if there are validation errors (e.g., required attributes missing)
      if (Object.keys(validationErrors).length > 0) {
        throw new Error("Fill all required attribute fields");
      }

      const changes = transformDirtyForSave(dirtyFields, _formData);
      return tiktokProductApi.saveChanges(connectionName, productId, changes);
    },

    onMutate() {
      useTikTokEditStore.getState().setSaveStatus("saving");
    },

    async onSuccess(data) {
      const store = useTikTokEditStore.getState();
      const message = getSaveMessage(data.save_type);

      // Clear dirty fields but keep success status visible (component handles fade)
      useTikTokEditStore.setState({
        dirtyFields: {},
        saveStatus: "success",
        saveMessage: message,
      });

      // Invalidate the listing doc query and WAIT for refetch to complete
      if (store.listingName) {
        await queryClient.invalidateQueries({
          queryKey: ["get", "TikTok Listing", store.listingName],
        });
      }

      // Read fresh data directly from cache (refetch already completed above)
      // and re-init the store immediately — don't rely on component re-render
      // cycle which may still have stale formData from StandardDetailPage state
      const freshData = queryClient.getQueryData<Record<string, any>>(
        ["get", "TikTok Listing", store.listingName],
      );
      if (freshData) {
        store.forceReinit();
        store.init(
          freshData,
          store.connectionName,
          store.productId,
          store.listingName,
        );
      } else {
        store.forceReinit();
      }
    },

    onError(error) {
      const msg = error?.message || "Save failed — tap to retry";
      useTikTokEditStore.getState().setSaveStatus("error", msg);
    },
  });
}
